import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider as ReactRouterProvider,
} from 'react-router-dom';
import AuthGuard from '../components/AuthGuard/AuthGuard';
import ManagementView from '../components/ManagementGrid/ManagementView';
import { S3GRole } from '../generated-types/authRoles';
import Layout from '../layout/Layout';
import AboutView from '../views/AboutView/AboutView';
import AdminView from '../views/AdminDashboardView/AdminDashboardView';
import ArticlesSubview from '../views/AdminDashboardView/Subviews/Articles/ArticlesSubview';
import ClientOnboardingSubview from '../views/AdminDashboardView/Subviews/ClientOnboardingSubview';
import HomeSubview from '../views/AdminDashboardView/Subviews/HomeSubview';
import MessagesSubview from '../views/AdminDashboardView/Subviews/Messages/MessagesSubview';
import OrganizationsSubview from '../views/AdminDashboardView/Subviews/Organizations/OrganizationsSubview';
import UtilitiesSubview from '../views/AdminDashboardView/Subviews/Utilities/UtilitiesSubview';
import ArticlesView from '../views/ArticlesView/ArticlesView';
import Article from '../views/ArticlesView/Components/Article';
import ArticleList from '../views/ArticlesView/Components/ArticleList';
import ChangePasswordView from '../views/ChangePasswordView/ChangePasswordView';
import ClientOnboardingView from '../views/ClientOnboardingView/ClientOnboardingView';
import ContactView from '../views/ContactView/ContactView';
import CourseView from '../views/CourseView/CourseView';
import ForgotPasswordView from '../views/ForgotPasswordView/ForgotPasswordView';
import HomeView from '../views/HomeView/HomeView';
import InactiveLicenseView from '../views/InactiveLicenseView/InactiveLicenseView';
import LegalView from '../views/LegalView/LegalView';
import LoginView from '../views/LoginView/LoginView';
import NotFoundView from '../views/NotFoundView/NotFoundView';
import PurchaseTraining from '../views/PurchaseTraining/PurchaseTraining';
import SignupView from '../views/SignupView/SignupView';
import TrainingView from '../views/TrainingView/TrainingView';
import WelcomeView from '../views/WelcomeView/WelcomeView';

/**
 * The router provider component, which includes all the S3G routes
 */
const RouterProvider = () => {
  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route path="/" element={<Layout />}>
        <Route index element={<HomeView />} />
        <Route path="articles" element={<ArticlesView />}>
          <Route index element={<ArticleList />} />
          <Route path=":slug" element={<Article />} />
        </Route>
        <Route
          path="training/courses/:courseSlug"
          element={
            <AuthGuard requiredRoles={[S3GRole.User]} unauthToLogin>
              <CourseView />
            </AuthGuard>
          }
        />
        <Route
          path="management"
          element={
            <AuthGuard requiredRoles={[S3GRole.Manager]} unauthToLogin>
              <ManagementView orgId={undefined} />
            </AuthGuard>
          }
        />
        <Route
          path="training"
          element={
            <AuthGuard requiredRoles={[S3GRole.User]} unauthToLogin>
              <TrainingView />
            </AuthGuard>
          }
        />
        <Route
          path="change-password"
          element={
            <AuthGuard unauthToLogin>
              <ChangePasswordView />
            </AuthGuard>
          }
        />
        <Route
          path="admin"
          element={
            <AuthGuard requiredRoles={[S3GRole.Admin]} unauthToLogin>
              <AdminView />
            </AuthGuard>
          }
        >
          <Route index element={<HomeSubview />} />
          <Route
            path="client-onboarding"
            element={<ClientOnboardingSubview />}
          />
          <Route path="articles" element={<ArticlesSubview />} />
          <Route path="messages" element={<MessagesSubview />} />
          <Route path="organizations" element={<OrganizationsSubview />} />
          <Route path="utilities" element={<UtilitiesSubview />} />
        </Route>
        <Route
          path="client-onboarding"
          element={
            <AuthGuard requiredRoles={[S3GRole.ClientOnboarding]} unauthToLogin>
              <ClientOnboardingView />
            </AuthGuard>
          }
        />
        <Route
          path="welcome"
          element={
            <AuthGuard onlyNoRoles unauthToLogin>
              <WelcomeView />
            </AuthGuard>
          }
        />
        <Route
          path="inactive-license"
          element={
            <AuthGuard requiredRoles={[S3GRole.User]} unauthToLogin>
              <InactiveLicenseView />
            </AuthGuard>
          }
        />
        <Route path="signup" element={<SignupView />} />
        <Route path="login" element={<LoginView />} />
        <Route path="about" element={<AboutView />} />
        <Route path="purchase-training" element={<PurchaseTraining />} />
        <Route
          path="terms-of-use"
          element={
            <LegalView pageId="terms-of-use" errorContext="terms of use" />
          }
        />
        <Route
          path="privacy-policy"
          element={
            <LegalView pageId="privacy-policy" errorContext="privacy policy" />
          }
        />
        <Route path="forgot-password" element={<ForgotPasswordView />} />
        <Route path="contact" element={<ContactView />} />

        {/* Catch-All Route */}
        <Route path="*" element={<NotFoundView />} />
      </Route>
    )
  );

  return <ReactRouterProvider router={router} />;
};

export default RouterProvider;
